import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { CheckCircle2 } from 'lucide-react';
import { graphql } from '@/gql';
import type { ThemePreference as GqlThemePreference, UserType } from '@/gql/graphql';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { refuseAll, screenAll, useEmailGuard } from '@/lib/useEmailCheck';
import { SignOutControls } from '@/features/session/SignOutControls';
import { useSession } from '@/features/session/useSession';
import { useTheme } from '@/layout/useTheme';

const UpdateMyProfileDoc = graphql(/* GraphQL */ `
  mutation UpdateMyProfile($input: UpdateProfileInput!) {
    updateMyProfile(input: $input) {
      id
      email
      firstName
      lastName
      themePreference
      defaultPage
      defaultScope {
        id
        name
      }
      personal {
        lastName
        dateOfBirth
        dateOfBirthUpdatedAt
        phone
        address
        postalCode
        city
        country
        gender
        pronouns
      }
      business {
        iban
        chamberOfCommerce
        vatNumber
        invoiceEmail
      }
    }
  }
`);

const MyVerticalProfileDoc = graphql(/* GraphQL */ `
  query MyVerticalProfile {
    me {
      type
      personal {
        lastName
        dateOfBirth
        dateOfBirthUpdatedAt
        phone
        address
        postalCode
        city
        country
        gender
        pronouns
      }
      business {
        iban
        chamberOfCommerce
        vatNumber
        invoiceEmail
      }
    }
  }
`);

const THEME_OPTIONS: { value: GqlThemePreference; label: string }[] = [
  { value: 'SYSTEM', label: 'Match device' },
  { value: 'LIGHT', label: 'Light' },
  { value: 'DARK', label: 'Dark' },
];

const PAGE_OPTIONS = [
  { value: '', label: 'Role default' },
  { value: '/', label: 'Turnover dashboard' },
  { value: '/orders', label: 'Orders' },
  { value: '/tabs', label: 'Tabs' },
  { value: '/charts', label: 'Charts' },
  { value: '/payments', label: 'Payments' },
];

function dobLocked(updatedAt: string | null | undefined): boolean {
  if (!updatedAt) return false;
  const at = new Date(updatedAt).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at < 365 * 24 * 60 * 60 * 1000;
}

function showsBusiness(type: UserType | null | undefined): boolean {
  return type === 'EMPLOYEE' || type === 'CONTRACTOR';
}

/**
 * Lets the signed-in user manage their email, landing page, theme preference
 * and default scope. Everything is stored server-side on the User entity; the
 * theme preference is mirrored to localStorage via `useTheme` so the next
 * page load picks it up before the Me query returns.
 */
export function ProfilePage() {
  const { me, refresh, loading } = useSession();
  const { setPreference } = useTheme();
  const { data: vertical, refetch: refetchVertical } = useQuery(MyVerticalProfileDoc, {
    skip: !me,
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network',
  });
  const personal = vertical?.me.personal;
  const business = vertical?.me.business;
  const userType = vertical?.me.type ?? me?.type;
  const [email, setEmail] = useState(() => me?.email ?? '');
  const [firstName, setFirstName] = useState(() => me?.firstName ?? '');
  const [lastName, setLastName] = useState(() => me?.lastName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [gender, setGender] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [iban, setIban] = useState('');
  const [chamberOfCommerce, setChamberOfCommerce] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [themePreference, setThemePreference] = useState<GqlThemePreference>(
    () => (me?.themePreference ?? 'SYSTEM') as GqlThemePreference,
  );
  const [defaultPage, setDefaultPage] = useState(() => me?.defaultPage ?? '');
  const [defaultScopeId, setDefaultScopeId] = useState<string>(
    () => me?.defaultScope?.id ?? '',
  );
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(me?.id ?? null);
  const [seededPersonal, setSeededPersonal] = useState(false);
  if (me && me.id !== seededFor) {
    setSeededFor(me.id);
    setSeededPersonal(false);
    setEmail(me.email ?? '');
    setFirstName(me.firstName ?? '');
    setLastName(me.lastName ?? '');
    setThemePreference((me.themePreference ?? 'SYSTEM') as GqlThemePreference);
    setDefaultPage(me.defaultPage ?? '');
    setDefaultScopeId(me.defaultScope?.id ?? '');
  }
  if (personal && !seededPersonal) {
    setSeededPersonal(true);
    setLastName(personal.lastName ?? me?.lastName ?? '');
    setDateOfBirth(personal.dateOfBirth ?? '');
    setPhone(personal.phone ?? '');
    setAddress(personal.address ?? '');
    setPostalCode(personal.postalCode ?? '');
    setCity(personal.city ?? '');
    setCountry(personal.country ?? '');
    setGender(personal.gender ?? '');
    setPronouns(personal.pronouns ?? '');
    setIban(business?.iban ?? '');
    setChamberOfCommerce(business?.chamberOfCommerce ?? '');
    setVatNumber(business?.vatNumber ?? '');
    setInvoiceEmail(business?.invoiceEmail ?? '');
  }

  const [save, { loading: mutating }] = useMutation(UpdateMyProfileDoc);
  const [checking, setChecking] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const emailGuard = useEmailGuard();
  const invoiceEmailGuard = useEmailGuard();
  const saving = mutating || checking;
  const dobIsLocked = dobLocked(personal?.dateOfBirthUpdatedAt) && Boolean(personal?.dateOfBirth);

  const scopeOptions = useMemo(
    () => [
      { value: '', label: 'No default (use global default)' },
      ...(me?.allowedScopes.map((scope) => ({ value: scope.id, label: scope.name })) ?? []),
    ],
    [me?.allowedScopes],
  );

  const roleLabels = useMemo(() => {
    const bindings = me?.roleBindings ?? [];
    if (bindings.length === 0) return [];
    return bindings.map((b) =>
      b.scope?.name ? `${b.role.name} (${b.scope.name})` : b.role.name,
    );
  }, [me?.roleBindings]);

  if (loading && !me) {
    return <p className="text-sm text-gray-500">Loading your profile…</p>;
  }
  if (!me) {
    return <p className="text-sm text-gray-500">You are not signed in.</p>;
  }

  const persist = async (next: { email: string; invoiceEmail: string }) => {
    setFormError(null);
    setChecking(true);
    try {
      const screened = await screenAll([
        [emailGuard, next.email !== (me.email ?? '') ? next.email : null],
        [
          invoiceEmailGuard,
          showsBusiness(userType) && next.invoiceEmail !== (business?.invoiceEmail ?? '')
            ? next.invoiceEmail
            : null,
        ],
      ]);
      if (!screened) return;
      await save({
        variables: {
          input: {
            email: next.email || null,
            firstName: firstName.trim() || null,
            lastName: lastName.trim() || null,
            themePreference,
            defaultPage: defaultPage || null,
            defaultScopeId: defaultScopeId || null,
            dateOfBirth: dateOfBirth.trim() || null,
            phone: phone.trim() || null,
            address: address.trim() || null,
            postalCode: postalCode.trim() || null,
            city: city.trim() || null,
            country: country.trim() || null,
            gender: gender.trim() || null,
            pronouns: pronouns.trim() || null,
            iban: showsBusiness(userType) ? iban.trim() || null : undefined,
            chamberOfCommerce: showsBusiness(userType)
              ? chamberOfCommerce.trim() || null
              : undefined,
            vatNumber: showsBusiness(userType) ? vatNumber.trim() || null : undefined,
            invoiceEmail: showsBusiness(userType) ? next.invoiceEmail || null : undefined,
          },
        },
      });
      setPreference(themePreference.toLowerCase() as 'light' | 'dark' | 'system');
      await refresh();
      await refetchVertical();
      setSavedAt(new Date());
    } catch (err) {
      refuseAll(err, [
        [emailGuard, next.email],
        [invoiceEmailGuard, next.invoiceEmail],
      ]);
      setFormError(graphqlErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await persist({ email: email.trim(), invoiceEmail: invoiceEmail.trim() });
  };

  /** Use / Keep on a prompt: set the field to the picked address and save at once. */
  const resubmit = (field: 'email' | 'invoiceEmail', address: string) => {
    if (field === 'email') setEmail(address);
    else setInvoiceEmail(address);
    void persist({
      email: field === 'email' ? address : email.trim(),
      invoiceEmail: field === 'invoiceEmail' ? address : invoiceEmail.trim(),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Your profile
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage the preferences that personalise your dashboard experience.
          </p>
        </div>
        <SignOutControls />
      </div>

      <FormLayout
        title="Account"
        description="Signed in as a passkey account. Role assignments can only be changed by an administrator."
        onSubmit={handleSubmit}
        footer={
          <div className="flex items-center justify-between gap-3">
            {savedAt && !saving && !formError ? (
              <span className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Saved {savedAt.toLocaleTimeString()}
              </span>
            ) : formError ? (
              <span className="text-sm text-red-600 dark:text-red-400">{formError}</span>
            ) : (
              <span />
            )}
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save profile'}
            </Button>
          </div>
        }
      >
        <FormSection
          title="Identity"
          description="Email is the only login identifier. Marketing opt-in is managed in ExpoApp and imports, not here."
        >
          <FormField label="First name" required>
            <Input
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Last name">
            <Input
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </FormField>
          <FormField
            label="Date of birth"
            description={
              dobIsLocked
                ? 'Date of birth can only be changed once every 365 days.'
                : 'Used for ticket age and door display. Editable at most once per 365 days.'
            }
          >
            <Input
              type="date"
              value={dateOfBirth}
              onChange={(event) => setDateOfBirth(event.target.value)}
              disabled={dobIsLocked}
            />
          </FormField>
          <FormField label="Phone">
            <Input type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
          </FormField>
          <FormField label="Address">
            <Input value={address} onChange={(event) => setAddress(event.target.value)} />
          </FormField>
          <FormField label="Postal code">
            <Input value={postalCode} onChange={(event) => setPostalCode(event.target.value)} />
          </FormField>
          <FormField label="City">
            <Input value={city} onChange={(event) => setCity(event.target.value)} />
          </FormField>
          <FormField label="Country">
            <Input value={country} onChange={(event) => setCountry(event.target.value)} />
          </FormField>
          <FormField label="Gender">
            <Input value={gender} onChange={(event) => setGender(event.target.value)} />
          </FormField>
          <FormField label="Pronouns">
            <Input value={pronouns} onChange={(event) => setPronouns(event.target.value)} />
          </FormField>
          <FormField
            label="Email"
            description="Used as the login identifier and for passkey registration links."
          >
            <Input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                emailGuard.changed();
              }}
              placeholder="you@example.com"
            />
          </FormField>
          <GuardSuggestion
            guard={emailGuard}
            idPrefix="admin.profile.email"
            disabled={saving}
            onAnswer={(address) => resubmit('email', address)}
          />
        </FormSection>

        {showsBusiness(userType) ? (
          <FormSection
            title="Business"
            description="Synced to Exact Online as a contractor business relation after save. A sync failure is logged and does not undo this write."
          >
            <FormField label="IBAN">
              <Input value={iban} onChange={(event) => setIban(event.target.value)} />
            </FormField>
            <FormField label="Chamber of commerce (KvK)">
              <Input
                value={chamberOfCommerce}
                onChange={(event) => setChamberOfCommerce(event.target.value)}
              />
            </FormField>
            <FormField label="VAT number">
              <Input value={vatNumber} onChange={(event) => setVatNumber(event.target.value)} />
            </FormField>
            <FormField label="Invoice email">
              <Input
                type="email"
                value={invoiceEmail}
                onChange={(event) => {
                  setInvoiceEmail(event.target.value);
                  invoiceEmailGuard.changed();
                }}
              />
            </FormField>
            <GuardSuggestion
              guard={invoiceEmailGuard}
              idPrefix="admin.profile.invoiceEmail"
              disabled={saving}
              onAnswer={(address) => resubmit('invoiceEmail', address)}
            />
          </FormSection>
        ) : null}

        <FormSection
          title="Experience"
          description="Choose where you want to land after sign-in and how the interface should look."
        >
          <FormField label="Default page">
            <Select
              value={defaultPage}
              onChange={(event) => setDefaultPage(event.target.value)}
              options={PAGE_OPTIONS}
            />
          </FormField>
          <FormField label="Theme">
            <Select<GqlThemePreference>
              value={themePreference}
              onChange={(event) =>
                setThemePreference(event.target.value as GqlThemePreference)
              }
              options={THEME_OPTIONS}
            />
          </FormField>
          <FormField
            label="Default scope"
            description="Used on login when no scope is stored in your session."
          >
            <Select
              value={defaultScopeId}
              onChange={(event) => setDefaultScopeId(event.target.value)}
              options={scopeOptions}
            />
          </FormField>
        </FormSection>
      </FormLayout>

      <Card
        title="Sign-in history"
        subtitle="We only track the most recent successful sign-in."
      >
        <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Roles</dt>
            <dd className="mt-1 font-medium text-gray-900 dark:text-white">
              {roleLabels.length > 0 ? roleLabels.join(', ') : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Last sign-in</dt>
            <dd className="mt-1 font-medium text-gray-900 dark:text-white">
              {me.lastLoginAt
                ? new Date(me.lastLoginAt).toLocaleString()
                : 'Never'}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
